import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, CalendarDays, Clock, CreditCard, Banknote, CheckCircle, XCircle, Search } from "lucide-react";
import { BookingsGroupedView } from "@/components/BookingsGroupedView";

const statusStyle = { CONFIRMED: "bg-green-100 text-green-800", PENDING_CASH: "bg-amber-100 text-amber-800", PENDING_PAYMENT: "bg-amber-100 text-amber-800", CANCELLED: "bg-red-100 text-red-800", COMPLETED: "bg-blue-100 text-blue-800", NO_SHOW: "bg-slate-100 text-slate-800" };
const statusLabel = { CONFIRMED: "Confirmed", PENDING_CASH: "Pay at Venue", PENDING_PAYMENT: "Awaiting Pay", CANCELLED: "Cancelled", COMPLETED: "Completed", NO_SHOW: "No Show" };
const CANCEL_REASONS = ["Facility maintenance","Weather conditions","Emergency closure","Double booking error","Player request","Safety concern","Other"];

export default function ProviderBookings() {
  const [bookings, setBookings] = useState([]);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [detailBooking, setDetailBooking] = useState(null);
  const [detailWaitlist, setDetailWaitlist] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelOther, setCancelOther] = useState("");
  const [cancelForce, setCancelForce] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const fetchBookings = async () => {
    try {
      const [bookingsRes, waitlistRes] = await Promise.all([
        api.get("/provider/bookings"),
        api.get("/provider/waitlist"),
      ]);
      setBookings(bookingsRes.data);
      setWaitlistEntries(waitlistRes.data || []);
    } catch { setBookings([]); }
    setLoading(false);
  };

  useEffect(() => { fetchBookings(); }, []);

  const handleCashConfirm = async (bookingId) => {
    try { await api.post(`/bookings/${bookingId}/cash-confirm`); toast.success("Cash payment confirmed!"); fetchBookings(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget || !cancelReason) { toast.error("Please select a reason"); return; }
    const reason = cancelReason === "Other" ? (cancelOther.trim() || "Other") : cancelReason;
    setCancelling(true);
    try {
      await api.post(`/booking-policy/cancel/venue/${cancelTarget.booking_id}`, { reason, force_majeure: cancelForce });
      toast.success("Booking cancelled. Player will receive a full refund.");
      setCancelTarget(null); setCancelReason(""); setCancelOther(""); setCancelForce(false);
      fetchBookings();
    } catch (err) { toast.error(err.response?.data?.detail || "Cancellation failed"); }
    setCancelling(false);
  };

  // Client-side search filter
  const filtered = bookings.filter(b => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!b.user_name?.toLowerCase().includes(q) && !b.resource_name?.toLowerCase().includes(q) && !b.venue_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const renderBooking = (b) => (
    <div className="bg-white border rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`prov-booking-${b.booking_id}`}>
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading font-bold text-sm">{b.user_name || b.user_email}</h3>
          <Badge className={`${statusStyle[b.status] || "bg-muted"} text-[10px]`}>{statusLabel[b.status] || b.status}</Badge>
          <Badge variant="outline" className="text-[9px] capitalize">{b.sport_type}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">{b.resource_name} at {b.venue_name}</p>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{b.start_dt?.slice(0, 10)}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{b.start_dt?.slice(11, 16)} - {b.end_dt?.slice(11, 16)}</span>
          <span className="flex items-center gap-1">{b.payment_mode === "ONLINE" ? <CreditCard className="w-3 h-3" /> : <Banknote className="w-3 h-3" />}{b.payment_mode}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setDetailBooking(b)} className="rounded-full text-xs h-7 px-3" data-testid={`view-booking-${b.booking_id}`}>View</Button>
        <p className="font-heading text-lg font-bold text-primary">₱{b.total_amount?.toFixed(0)}</p>
        {b.status === "PENDING_CASH" && (
          <Button size="sm" onClick={() => handleCashConfirm(b.booking_id)} className="rounded-full text-xs h-7 px-3">
            <CheckCircle className="w-3 h-3 mr-1" />Confirm
          </Button>
        )}
        {["CONFIRMED", "PENDING_CASH", "PENDING_PAYMENT"].includes(b.status) && (
          <Button size="sm" variant="outline" onClick={() => { setCancelTarget(b); setCancelReason(""); setCancelOther(""); setCancelForce(false); }}
            className="rounded-full text-xs h-7 px-3 text-red-600 border-red-200 hover:bg-red-50" data-testid={`cancel-${b.booking_id}`}>
            <XCircle className="w-3 h-3 mr-1" />Cancel
          </Button>
        )}
      </div>
    </div>
  );

  const renderWaitlistItem = (entry) => (
    <div className="bg-white border rounded-lg p-4 flex items-center justify-between gap-3" data-testid={`waitlist-${entry.waitlist_id}`}>
      <div>
        <p className="font-medium text-sm">{entry.user_name || entry.user_email}</p>
        <p className="text-xs text-muted-foreground">{entry.resource_name} · {entry.desired_date} {entry.desired_start_time}-{entry.desired_end_time} · Position {entry.position}</p>
      </div>
      <Button size="sm" variant="outline" onClick={() => setDetailWaitlist(entry)} className="rounded-full text-xs">View</Button>
    </div>
  );

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6" data-testid="provider-bookings-page">
      <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-4">Bookings</h1>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search player, court, venue..." className="pl-9" data-testid="booking-search" />
      </div>

      <BookingsGroupedView
        bookings={filtered}
        renderItem={renderBooking}
        waitlistCount={waitlistEntries.length}
        waitlistItems={waitlistEntries}
        renderWaitlistItem={renderWaitlistItem}
      />

      {/* Cancel Dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={v => { if (!v) setCancelTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="venue-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-red-600">Cancel Booking</DialogTitle>
            <DialogDescription>This will cancel the booking and issue a full refund to the player.</DialogDescription>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                <p className="font-medium">{cancelTarget.user_name}</p>
                <p className="text-xs text-muted-foreground">{cancelTarget.resource_name} · {cancelTarget.start_dt?.slice(0, 10)} {cancelTarget.start_dt?.slice(11, 16)}-{cancelTarget.end_dt?.slice(11, 16)}</p>
                <p className="text-xs font-medium text-red-700 mt-1">Player will receive 100% refund (₱{cancelTarget.total_amount})</p>
              </div>
              <div><Label className="text-xs font-medium">Reason for Cancellation *</Label>
                <Select value={cancelReason} onValueChange={setCancelReason}>
                  <SelectTrigger data-testid="venue-cancel-reason"><SelectValue placeholder="Select a reason..." /></SelectTrigger>
                  <SelectContent>{CANCEL_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select></div>
              {cancelReason === "Other" && (
                <div><Label className="text-xs font-medium">Please specify</Label>
                  <Textarea value={cancelOther} onChange={e => setCancelOther(e.target.value)} placeholder="Type reason..." className="text-sm" /></div>
              )}
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={cancelForce} onChange={e => setCancelForce(e.target.checked)} id="force-majeure" />
                <label htmlFor="force-majeure" className="text-xs">Force Majeure (no venue reliability impact)</label>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setCancelTarget(null)} className="flex-1">Keep Booking</Button>
                <Button onClick={handleCancelConfirm} disabled={cancelling || !cancelReason} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="venue-confirm-cancel">
                  <XCircle className="w-4 h-4 mr-1" />{cancelling ? "Cancelling..." : "Confirm Cancellation"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailBooking} onOpenChange={v => { if (!v) setDetailBooking(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="provider-booking-detail-dialog">
          <DialogHeader><DialogTitle>Booking Details</DialogTitle></DialogHeader>
          {detailBooking && <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(detailBooking, null, 2)}</pre>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailWaitlist} onOpenChange={v => { if (!v) setDetailWaitlist(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="provider-waitlist-detail-dialog">
          <DialogHeader><DialogTitle>Waiting List Details</DialogTitle></DialogHeader>
          {detailWaitlist && <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(detailWaitlist, null, 2)}</pre>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
